// Shared React-Flow key-diagram → PDF capture routine.
//
// Extracted verbatim from AllKeyDiagrams.js so that BOTH the standalone
// "Export All to PDF" (/reports) AND the Répartition Report export
// (RepartitionReport/Preview.js) capture diagrams identically. React-Flow pages
// cannot be captured by the répartition orchestrator's naive toPng path — they
// need the full property list, edge-stroke forcing, .reactflow-capture-root
// targeting, header/footer compositing, and html2canvas fallback below.
//
// Public surface:
//   DIAGRAM_STYLE_PROPS                 — css props toPng must copy for ReactFlow
//   prefetchFontEmbedCSS(el)            — fetch font CSS once (slow), reuse per page
//   appendDiagramPagesToPdf({...})      — capture each diagram page into an existing
//                                         jsPDF; returns # of pages added

import html2canvas from "html2canvas";
import { toPng, getFontEmbedCSS } from "html-to-image";
import { getRectOfNodes } from "reactflow";

// CSS properties needed to render a ReactFlow diagram correctly.
// toPng normally reads ALL ~400 computed properties per element — limiting to these
// ~80 gives a ~5x speedup while still covering everything needed for accurate layout.
// CRITICAL: box-sizing must be included or ReactFlow nodes render at wrong sizes
// (border-box vs content-box mismatch shifts handle positions).
export const DIAGRAM_STYLE_PROPS = [
  // Box model — box-sizing is critical for correct node dimensions
  "box-sizing",
  "display", "visibility", "opacity", "overflow", "overflow-x", "overflow-y",
  "position", "top", "left", "right", "bottom", "z-index",
  "width", "height", "min-width", "min-height", "max-width", "max-height",
  // table-layout: MUST be here too. html-to-image caches the FIRST toPng call's
  // includeStyleProperties in a process-global singleton (getStyleProperties in
  // util.js) and reuses it for ALL later captures. If this diagram list (which the
  // export runs in phase 2, before the phase-2.5 Résultat détaillé capture) wins
  // the cache, a missing table-layout would strip `tableLayout:fixed` from the
  // détaillé tables → they reflow to auto layout and clip. Keep in sync with
  // TEMPLATE_STYLE_PROPS in exportRepartitionPdf.js.
  "table-layout",
  "margin", "margin-top", "margin-right", "margin-bottom", "margin-left",
  "padding", "padding-top", "padding-right", "padding-bottom", "padding-left",
  // Borders — individual sides needed because computed style reports per-side, not shorthand
  "border", "border-radius", "border-color", "border-width", "border-style",
  "border-top", "border-right", "border-bottom", "border-left",
  "border-top-width", "border-right-width", "border-bottom-width", "border-left-width",
  "border-top-style", "border-right-style", "border-bottom-style", "border-left-style",
  "border-top-color", "border-right-color", "border-bottom-color", "border-left-color",
  "border-top-left-radius", "border-top-right-radius", "border-bottom-left-radius", "border-bottom-right-radius",
  // Outline (focus rings)
  "outline", "outline-color", "outline-style", "outline-width", "outline-offset",
  // Background
  "background", "background-color", "background-image", "background-position", "background-size", "background-repeat",
  // Box shadow (ReactFlow nodes often have shadows)
  "box-shadow",
  // Text
  "color", "font", "font-size", "font-weight", "font-family", "font-style", "font-variant",
  "line-height", "letter-spacing", "word-spacing",
  "text-align", "text-decoration", "text-transform", "text-overflow", "white-space", "word-break", "word-wrap",
  "vertical-align",
  // Transforms — critical for ReactFlow node and viewport positioning
  "transform", "transform-origin", "transform-style",
  // Flex
  "flex", "flex-direction", "flex-wrap", "flex-grow", "flex-shrink", "flex-basis",
  "align-items", "align-content", "align-self", "justify-content", "justify-items", "justify-self",
  "gap", "row-gap", "column-gap", "order",
  // SVG
  "stroke", "stroke-width", "stroke-dasharray", "stroke-linecap", "stroke-linejoin", "stroke-opacity",
  "fill", "fill-opacity", "fill-rule",
  "marker-start", "marker-mid", "marker-end",
  // Misc
  "pointer-events", "cursor", "user-select",
];

const exportLog = (msg) => {
  const ts = new Date().toLocaleTimeString("en-GB", { hour12: false });
  console.log(`[Diagram PDF ${ts}] ${msg}`);
};

// Fetch font-embed CSS once (it's the slowest part of toPng — html-to-image
// otherwise re-fetches fonts on every call). Pass the result to
// appendDiagramPagesToPdf so every page reuses it.
export async function prefetchFontEmbedCSS(el) {
  if (!el) return "";
  try {
    return await getFontEmbedCSS(el);
  } catch (e) {
    exportLog(`Font CSS fetch failed (${e.message}), continuing without fonts`);
    return "";
  }
}

// Capture each diagram page (a `.diagram-a4-frame` DOM node produced by
// KeyDiagramView) into the supplied jsPDF instance, compositing header + flow +
// footer onto an A4 page. Mutates `pdf`. Returns the number of pages added.
//
//   pdf            jsPDF instance (portrait, pt, a4) — pages are appended
//   pages          [{ key, el, pi }]  el = `.diagram-a4-frame`, pi = sub-page index
//   fontEmbedCSS   string from prefetchFontEmbedCSS (optional)
//   onProgress     fn(done, total) (optional)
//   startPdfPageCount  how many pages already in the pdf (so the first appended
//                  page calls addPage instead of writing onto an existing page).
//                  Defaults to 0 (legacy AllKeyDiagrams behaviour where the pdf
//                  starts empty and the very first page reuses jsPDF's blank page).
// Compute the painted extent of a react-flow diagram, in the viewport's own
// (untransformed) coordinate space.
//
// X / Y come from getRectOfNodes(layoutNodes): the layout nodes carry the
// correct .position (React-Flow places nodes via a CSS `transform: translate(x,y)`,
// NOT via left/top, so DOM offsetLeft/offsetTop read ~0 and CANNOT be used for
// horizontal position — that mistake pushed the whole diagram off the right edge).
//
// getRectOfNodes under-sizes BOTH the bottom AND the right edge: layout nodes carry
// a style `minHeight`/`minWidth` but no measured size, so variable-height nodes
// (long tenant lists in "Chauffage locataires"/"Eau chaude") are counted ~0 tall and
// variable-width nodes (chiefly the cost-details box, whose long labels overflow its
// layout width) are counted too narrow — cropping the tree's bottom and right. We
// correct BOTH maxY and maxX by reading each rendered node's MEASURED
// getBoundingClientRect() (transform-invariant → safe) and adding it to that node's
// layout X/Y, matching DOM↔layout by the `data-id` React-Flow stamps. The right-edge
// (maxX) correction matters most in LANDSCAPE capture (height-fit), where an
// under-measured width would otherwise clip the cost box.
//
// Returns { x, y, width, height } or null if getRectOfNodes yields nothing.
// Exported for unit testing (guards the "collapse to origin" regression).
export function diagramBounds(viewportEl, layoutNodes) {
  if (!Array.isArray(layoutNodes) || !layoutNodes.length) return null;
  const rect = getRectOfNodes(layoutNodes);
  if (!rect || !Number.isFinite(rect.x)) return null;

  let maxY = rect.y + rect.height; // getRectOfNodes' (under-sized) bottom
  let maxX = rect.x + rect.width;  // getRectOfNodes' (under-sized) right edge
  const els = viewportEl ? viewportEl.querySelectorAll(".react-flow__node") : [];
  if (els.length) {
    const posById = new Map(layoutNodes.map((n) => [String(n.id), n.position]));
    els.forEach((el) => {
      const pos = posById.get(String(el.getAttribute("data-id")));
      if (!pos) return;
      // getBoundingClientRect width/height are transform-invariant (React-Flow
      // positions nodes with CSS translate, and we capture at scale(1)), so both
      // are safe to add to the layout .position. Correct BOTH edges: the bottom
      // (tall trees) AND the right (wide variable-width nodes — chiefly the
      // cost-details box, whose long text overflows its layout width). Without the
      // maxX correction the landscape capture (height-fit) crops the cost box.
      const r = el.getBoundingClientRect();
      const h = r.height || el.offsetHeight || 0;
      const w = r.width || el.offsetWidth || 0;
      if (Number.isFinite(pos.y) && pos.y + h > maxY) maxY = pos.y + h;
      if (Number.isFinite(pos.x) && pos.x + w > maxX) maxX = pos.x + w;
    });
  }
  return { x: rect.x, y: rect.y, width: maxX - rect.x, height: maxY - rect.y };
}

export async function appendDiagramPagesToPdf({
  pdf,
  pages,
  fontEmbedCSS = "",
  onProgress = () => {},
  startPdfPageCount = 0,
}) {
  if (!pdf || !Array.isArray(pages) || pages.length === 0) return 0;

  const pixelsToPoints = 72 / 96;
  const SCALE = 4;
  // Key-diagram pages are ALWAYS rendered portrait (client request; a landscape
  // A4 is too short for a full 7-row tree and clips the bottom row). We add
  // every diagram page with pdf.addPage("a4","portrait") below, so the *canvas
  // compositing* math must use portrait dimensions too — otherwise the composite
  // ends up landscape-shaped and gets placed on a portrait page as an oversized
  // landscape image (right side clipped, bottom half empty).
  //
  // We cannot trust pdf.internal.pageSize.getWidth()/getHeight() here: the RR
  // export path leaves the pdf's "current page" on whichever orientation phase 1
  // ended with — often LANDSCAPE (Surfaces / Résultats sections). Reading the
  // current-page size before our addPage runs gives 842×595, and the composite
  // then drew landscape-sized onto a portrait page (client's page-17 bug).
  // AllKeyDiagrams starts from a fresh portrait pdf so it wasn't affected.
  //
  // Take min/max instead of the current-page dims so portrait is guaranteed
  // regardless of what the caller left on the pdf.
  const rawW = pdf.internal.pageSize.getWidth();
  const rawH = pdf.internal.pageSize.getHeight();
  const pageW = Math.min(rawW, rawH); // portrait width  (595)
  const pageH = Math.max(rawW, rawH); // portrait height (842)
  const margin = 24;

  // Collect all diagram wrapper divs so we can hide them during the html2canvas
  // fallback (reduces DOM traversal cost when many diagrams are on the page).
  const allWrappers = Array.from(document.querySelectorAll("[data-diagram-wrapper]"));

  let pdfPageCount = startPdfPageCount;
  let added = 0;

  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    const pageEntry = pages[pageIdx];
    const { key, pi } = pageEntry;
    // Backward-compat: a page's `el` may itself be a bare DOM node (legacy) or a
    // { el, nodes } wrapper. Normalise both so the capture sizing can use the
    // node coordinates when present, and fall back to the legacy box sizing when not.
    const diagramElement = pageEntry.el?.el || pageEntry.el;
    const diagramNodes = pageEntry.nodes || pageEntry.el?.nodes || [];
    const pageLabel = `page ${pageIdx + 1}/${pages.length} (key=${key?.id} sub=${pi})`;
    const t0 = Date.now();
    onProgress(pageIdx, pages.length);

    try {
      // ── 1. Basic DOM prep ──
      // NOTE: do NOT hide other wrappers here. toPng only serialises the target
      // element's subtree so other DOM elements don't affect it. Hiding wrappers
      // triggers a ReactFlow ResizeObserver → async re-layout cycle which moves
      // connection dots to wrong positions before the capture runs.
      const controls = diagramElement.querySelector(".react-flow__controls");
      const minimap = diagramElement.querySelector(".react-flow__minimap");
      if (controls) controls.style.display = "none";
      if (minimap) minimap.style.display = "none";

      const diagramBody = diagramElement.querySelector(".diagram-report-body");
      const origOverflow = diagramBody ? diagramBody.style.overflow : "";
      if (diagramBody) diagramBody.style.overflow = "visible";

      // ── 2. toPng with minimal CSS properties (the root-cause fix) ──
      const tPng = Date.now();

      let flowPNGData = null;
      // Set when we override the viewport transform below — restored in the
      // edge-stroke `finally` so a toPng throw can't leave the live preview shifted.
      let viewportEl = null;
      let savedViewportTransform = null;
      try {
        // The PAINTED extent of a wide graph (rightmost node column + cost box)
        // overflows the .reactflow-capture-root / .diagram-report-body box, which is
        // overflow:hidden/visible — so sizing the capture from that box (or from
        // scrollWidth, which doesn't grow for overflow:visible content) clips it.
        // Instead, capture the .react-flow__viewport at IDENTITY transform and size
        // it from the node coordinates (getRectOfNodes). The viewport's children are
        // absolutely positioned at natural graph coords, so identity scale paints the
        // whole tree at full size and toPng emits it complete. This is independent of
        // fitView/layout timing. The layout's invisible spacer node (x=1440) + measured
        // cost-details node keep the bbox wide, so the right edge is always inside it.
        viewportEl = diagramElement.querySelector(".react-flow__viewport");

        let rendererEl;
        let captureWidth;
        let captureHeight;

        if (viewportEl && diagramNodes.length) {
          const PAD = 24;
          // X/width/Y from the layout node .positions (correct — React-Flow places
          // nodes by CSS transform, so DOM offsetLeft is unusable), with only the
          // bottom edge corrected from measured DOM heights so tall trees
          // ("Chauffage locataires", "Eau chaude", the cost-details box) aren't
          // cropped. See diagramBounds().
          const b = diagramBounds(viewportEl, diagramNodes) || getRectOfNodes(diagramNodes);
          // Capture the graph's natural bounding box + PAD on every side. The
          // landscape composite below contain-fits this (near-square) box into the
          // landscape A4 band, where it fills the page — no aspect padding needed.
          captureWidth = Math.ceil(b.width + PAD * 2);
          captureHeight = Math.ceil(b.height + PAD * 2);
          savedViewportTransform = viewportEl.style.transform;
          viewportEl.style.transform = `translate(${-b.x + PAD}px, ${-b.y + PAD}px) scale(1)`;
          rendererEl = viewportEl;
        } else {
          // Legacy fallback (no node coords available): capture the container box.
          rendererEl =
            diagramElement.querySelector(".reactflow-capture-root") ||
            viewportEl ||
            diagramElement.querySelector(".react-flow__renderer") ||
            diagramElement.querySelector(".diagram-report-body") ||
            diagramElement;
          const rect = rendererEl.getBoundingClientRect();
          captureWidth = Math.ceil(Math.max(rect.width, rendererEl.scrollWidth || 0));
          captureHeight = Math.ceil(Math.max(rect.height, rendererEl.scrollHeight || 0));
        }

        // Temporarily force grey strokes so toPng captures them
        const restoreEdges = [];
        rendererEl.querySelectorAll(".react-flow__edge path, .react-flow__edge-path").forEach((p) => {
          restoreEdges.push({
            el: p,
            stroke: p.getAttribute("stroke"), sw: p.getAttribute("stroke-width"),
            dash: p.getAttribute("stroke-dasharray"),
            sStroke: p.style.stroke, sSW: p.style.strokeWidth,
          });
          p.setAttribute("stroke", "#b1b1b7");
          p.setAttribute("stroke-width", String(parseFloat(p.getAttribute("stroke-width") || "2") || 2));
          p.removeAttribute("stroke-dasharray");
          p.style.stroke = "#b1b1b7";
        });

        try {
          // KEY FIX: includeStyleProperties limits css copying to ~80 props instead of ~400.
          flowPNGData = await toPng(rendererEl, {
            cacheBust: false,
            pixelRatio: 5,
            fontEmbedCSS,
            includeStyleProperties: DIAGRAM_STYLE_PROPS,
            width: captureWidth,
            height: captureHeight,
            backgroundColor: "#ffffff",
          });
        } finally {
          restoreEdges.forEach(({ el: p, stroke, sw, dash, sStroke, sSW }) => {
            if (stroke === null) p.removeAttribute("stroke"); else p.setAttribute("stroke", stroke);
            if (sw === null) p.removeAttribute("stroke-width"); else p.setAttribute("stroke-width", sw);
            if (dash == null) p.removeAttribute("stroke-dasharray"); else p.setAttribute("stroke-dasharray", dash);
            p.style.stroke = sStroke || "";
            p.style.strokeWidth = sSW || "";
          });
          // Restore the viewport transform we overrode for the identity-scale capture
          // (even if toPng threw) so the on-screen preview is never left shifted.
          if (viewportEl && savedViewportTransform !== null) {
            viewportEl.style.transform = savedViewportTransform;
          }
        }
      } catch (e) {
        // Safety: if anything before the inner finally threw, still restore the transform.
        if (viewportEl && savedViewportTransform !== null) {
          viewportEl.style.transform = savedViewportTransform;
        }
        exportLog(`${pageLabel} — toPng FAILED (${e.message}), will use html2canvas fallback`);
      }

      exportLog(`${pageLabel} — toPng done in ${Date.now() - tPng}ms, flowPNGData=${!!flowPNGData}`);

      // ── 3. Composite path (toPng succeeded) ──
      if (flowPNGData) {
        try {
          const headerEl = diagramElement.querySelector(".diagram-report-header");
          const footerEl = diagramElement.querySelector(".diagram-report-footer");

          // Use toPng (with includeStyleProperties) for header/footer — same optimisation as flow.
          const toPngOpts = { pixelRatio: 4, fontEmbedCSS, cacheBust: false, includeStyleProperties: DIAGRAM_STYLE_PROPS, backgroundColor: "#ffffff" };
          const [headerPNG, footerPNG] = await Promise.all([
            headerEl ? toPng(headerEl, toPngOpts).catch(() => null) : Promise.resolve(null),
            footerEl ? toPng(footerEl, toPngOpts).catch(() => null) : Promise.resolve(null),
          ]);

          // Load header/footer/flow PNGs into Image objects for compositing
          const loadImg = (src) => (src ? new Promise((res) => {
            const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = src;
          }) : Promise.resolve(null));
          const [headerImg, footerImg, flowImg] = await Promise.all([
            loadImg(headerPNG), loadImg(footerPNG), loadImg(flowPNGData),
          ]);

          const pageScale = 4;
          const cw = Math.floor(pageW * pageScale); // landscape width
          const ch = Math.floor(pageH * pageScale); // landscape height
          const mPx = Math.floor(24 * pageScale);
          const comp = document.createElement("canvas");
          comp.width = cw; comp.height = ch;
          const ctx = comp.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, cw, ch);

          let y = mPx;
          if (headerImg) {
            const sc = Math.min((cw - mPx * 2) / headerImg.width, 1);
            const hw = Math.floor(headerImg.width * sc);
            const hh = Math.floor(headerImg.height * sc);
            ctx.drawImage(headerImg, Math.floor((cw - hw) / 2), y, hw, hh);
            y += hh + Math.floor(8 * pageScale);
          }

          const img = flowImg;

          let footerH = 0;
          if (footerImg) {
            const sc = Math.min((cw - mPx * 2) / footerImg.width, 1);
            footerH = Math.floor(footerImg.height * sc) + Math.floor(8 * pageScale);
          }
          const availH = ch - mPx - footerH - y;
          if (img) {
            // Fit the graph inside the available band (width AND height), preserving
            // aspect ratio, then CENTER it on BOTH axes so it sits in the middle of
            // the page — not pinned to the top-left with all the empty space dumped at
            // the bottom (the "graph not centered / big bottom gap" the client saw).
            // A small safety factor (0.98) keeps a margin so the widest column never
            // touches/overflows the page edge (the "right side cut" symptom).
            const fitW = cw - mPx * 2;
            const sFlow = Math.min(fitW / img.width, availH / img.height) * 0.98;
            const drawW = Math.floor(img.width * sFlow);
            const drawH = Math.floor(img.height * sFlow);
            const drawX = Math.floor((cw - drawW) / 2);            // horizontal center
            // Top-align the tree instead of vertically centering. When the layout
            // aspect is wider than portrait A4's band (typical case with a short
            // cost box + 6-row tree), width limits the scale and drawH < availH,
            // leaving a vertical gap. Centering split that gap 50/50 → visible
            // whitespace above the cost box that the client flagged as "empty
            // top of page". Top-aligning parks all leftover space at the bottom
            // (reads as natural page-end), so the cost box sits right under the
            // header and the tree cascades down to fill the top → middle of the
            // page. Height-limited layouts (very tall cost box) fill availH
            // exactly, so this branch is a no-op there.
            const drawY = y;
            ctx.drawImage(img, drawX, drawY, drawW, drawH);
          }

          if (footerImg) {
            const sc = Math.min((cw - mPx * 2) / footerImg.width, 1);
            const fw2 = Math.floor(footerImg.width * sc);
            const fh2 = Math.floor(footerImg.height * sc);
            ctx.drawImage(footerImg, Math.floor((cw - fw2) / 2), ch - mPx - fh2, fw2, fh2);
          }

          const pageImg = comp.toDataURL("image/jpeg", 0.95);
          if (pdfPageCount > 0) pdf.addPage("a4", "portrait");
          pdf.addImage(pageImg, "JPEG", 0, 0, pageW, pageH, undefined, "FAST");
          pdfPageCount++;
          added++;

          // Restore and continue to next page
          if (controls) controls.style.display = "";
          if (minimap) minimap.style.display = "";
          if (diagramBody) diagramBody.style.overflow = origOverflow;
          continue;
        } catch (e) {
          exportLog(`${pageLabel} — composite FAILED (${e.message}), falling back to full html2canvas`);
        }
      }

      // ── 4. Fallback: full-element html2canvas ──
      // Only here do we hide other wrappers — html2canvas traverses the full document
      // so a smaller DOM speeds it up. We wait 600ms after hiding so ReactFlow can
      // finish its resize re-layout before the capture runs.
      const tFB = Date.now();
      allWrappers.forEach((w) => { w.style.display = w.contains(diagramElement) ? "" : "none"; });
      await new Promise((resolve) => setTimeout(resolve, 600));

      const canvas = await html2canvas(diagramElement, {
        backgroundColor: "#ffffff",
        scale: SCALE,
        useCORS: true,
        allowTaint: true,
        logging: false,
        removeContainer: false,
        onclone: (doc) => {
          const s = doc.createElement("style");
          s.textContent = `
            .react-flow__edges,.react-flow__edge{opacity:1!important;visibility:visible!important;display:block!important;overflow:visible!important}
            .react-flow__edge path{opacity:1!important;visibility:visible!important;stroke-width:2px!important}
            svg{opacity:1!important;visibility:visible!important;display:block!important;overflow:visible!important}
          `;
          doc.head.appendChild(s);
          doc.querySelector(".diagram-report-body")?.style.setProperty("overflow", "visible", "important");
          doc.querySelector(".react-flow__renderer")?.style.setProperty("overflow", "visible", "important");
          doc.querySelector(".react-flow__viewport")?.style.setProperty("overflow", "visible", "important");
          doc.querySelector(".diagram-report-header")?.style.setProperty("width", "100%", "important");
          doc.querySelector(".diagram-report-footer")?.style.setProperty("width", "100%", "important");
          doc.querySelectorAll(".react-flow__edge").forEach((e) => { e.style.opacity = "1"; e.style.visibility = "visible"; e.style.overflow = "visible"; });
          doc.querySelectorAll(".react-flow__edge path").forEach((p) => {
            p.setAttribute("stroke", "#b1b1b7"); p.setAttribute("stroke-width", "2");
            p.setAttribute("fill", "none"); p.setAttribute("opacity", "1");
            p.removeAttribute("stroke-dasharray");
            p.style.stroke = "#b1b1b7"; p.style.fill = "none"; p.style.opacity = "1";
          });
          doc.querySelectorAll("svg").forEach((svg) => {
            const w = svg.getAttribute("width") || svg.style.width || "100%";
            const h = svg.getAttribute("height") || svg.style.height || "100%";
            svg.setAttribute("width", w); svg.setAttribute("height", h);
            svg.style.overflow = "visible"; svg.setAttribute("opacity", "1");
            svg.querySelectorAll("path").forEach((p) => {
              if (!p.getAttribute("stroke") || p.getAttribute("stroke") === "none") p.setAttribute("stroke", "#b1b1b7");
              if (!p.getAttribute("stroke-width") || p.getAttribute("stroke-width") === "0") p.setAttribute("stroke-width", "2");
              p.setAttribute("opacity", "1"); p.setAttribute("fill", "none");
            });
          });
          doc.querySelectorAll("marker").forEach((m) => m.setAttribute("opacity", "1"));
          doc.querySelectorAll(".edge-label-html").forEach((l) => { l.style.opacity = "1"; l.style.visibility = "visible"; });
        },
        ignoreElements: (el) => el.classList?.contains("react-flow__controls") || el.classList?.contains("react-flow__minimap"),
      });

      exportLog(`${pageLabel} — fallback html2canvas done in ${Date.now() - tFB}ms`);

      if (controls) controls.style.display = "";
      if (minimap) minimap.style.display = "";
      if (diagramBody) diagramBody.style.overflow = origOverflow;
      allWrappers.forEach((w) => { w.style.display = ""; }); // restore after fallback hide

      const imageData = canvas.toDataURL("image/jpeg", 0.95);
      const vwPx = canvas.width / SCALE;
      const vhPx = canvas.height / SCALE;
      const vwPt = vwPx * pixelsToPoints;
      const vhPt = vhPx * pixelsToPoints;
      const cWPt = pageW - margin * 2;
      const cHPt = pageH - margin * 2;
      const sc = Math.min(cWPt / vwPt, cHPt / vhPt) * 0.98; // 0.98 safety margin so nothing touches the edge
      if (pdfPageCount > 0) pdf.addPage("a4", "portrait");
      // Top-align vertically (see composite path for full rationale). Horizontally
      // still centered. Fallback path only runs when toPng fails — kept consistent
      // with the composite so the two paths produce visually identical output.
      pdf.addImage(imageData, "JPEG", (pageW - vwPt * sc) / 2, margin, vwPt * sc, vhPt * sc, undefined, "FAST");
      pdfPageCount++;
      added++;

      exportLog(`${pageLabel} — complete in ${Date.now() - t0}ms total`);
    } catch (err) {
      allWrappers.forEach((w) => { w.style.display = ""; }); // safety restore on error
      exportLog(`${pageLabel} — ERROR: ${err.message}`);
      console.error(`Error on ${pageLabel}:`, err);
    }
  }

  onProgress(pages.length, pages.length);
  return added;
}
